// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import { useMemo, useCallback } from 'react';
import equal from 'deep-equal';
import rfdc from 'rfdc';
import { AgGridUtils } from '@cosmotech/core';
import { UPLOAD_FILE_STATUS_KEY } from '@cosmotech/ui';
import { Api } from '../../../services/config/Api';
import { TECHNICAL_POLICY_COLUMNS } from '../../../services/technicalPolicy/technicalPolicyColumns';
import { useAddDatasetToStore } from '../../../state/hooks/DatasetHooks';
import { useOrganizationId } from '../../../state/hooks/OrganizationHooks';
import { useCurrentScenarioData, useScenarios, useSetCurrentScenario } from '../../../state/hooks/ScenarioHooks';
import { useSolution } from '../../../state/hooks/SolutionHooks';
import { useWorkspaceId } from '../../../state/hooks/WorkspaceHooks';
import {
  useGestionActivites,
  useGisementById,
  useGisements,
  useNiveauDePriorisation,
  useSetWizardQueriesStatus,
  useUpdateGisementData,
  useUpdateTechnicalPolicy,
} from '../../../state/wizard/WizardHooks';
import { ApiUtils, ScenarioParametersUtils } from '../../../utils';
import { FileManagementUtils } from '../../../utils/FileManagementUtils';
import { WizardUtils } from '../../../utils/WizardUtils';

const clone = rfdc();

const getRunTemplateParametersIds = (runTemplatesParametersIdsDict, runTemplateId) => {
  return runTemplatesParametersIdsDict?.[runTemplateId] ?? [];
};

export const useSaveWizardData = (selectedGisement) => {
  const solutionData = useSolution().data;
  const currentScenarioData = useCurrentScenarioData();
  const updateScenario = useSetCurrentScenario();
  const organizationId = useOrganizationId();
  const workspaceId = useWorkspaceId();
  const addDatasetToStore = useAddDatasetToStore();
  const scenarios = useScenarios();
  const setWizardQueriesStatus = useSetWizardQueriesStatus();
  const gisementData = useGisementById(currentScenarioData.id, selectedGisement);
  const allGisements = useGisements(currentScenarioData.id);
  const gestionActivites = useGestionActivites(currentScenarioData.id);
  const updateGisementData = useUpdateGisementData();
  const niveauDePriorisation = useNiveauDePriorisation(currentScenarioData.id);
  const updateTechnicalPolicy = useUpdateTechnicalPolicy();
  const runTemplateParametersIds = useMemo(
    () => getRunTemplateParametersIds(solutionData?.runTemplatesParametersIdsDict, currentScenarioData?.runTemplateId),
    [solutionData?.runTemplatesParametersIdsDict, currentScenarioData?.runTemplateId]
  );
  const technicalPolicyParameter = useMemo(() => {
    return solutionData.parameters?.find((parameter) => parameter.options?.subType === 'TECHNICALPOLICY');
  }, [solutionData.parameters]);

  const saveData = useCallback(
    async (values, reset, setSaveTechnicalPolicyError, newTableNamesMapping = undefined) => {
      try {
        setWizardQueriesStatus('SAVING');
        const tableNamesMapping = newTableNamesMapping ?? gisementData?.tableNamesMapping;
        const parametersValues = {};
        const metadata = {};
        const valuesToSet = [];
        const existingGisementsList = gestionActivites.map((row) => row.Gisement);
        if (values.NiveauDePriorisation && !equal(values.NiveauDePriorisation, niveauDePriorisation)) {
          const columns = WizardUtils.getColumnsFormatedForAgGridUtils(TECHNICAL_POLICY_COLUMNS.NiveauDePriorisation());
          const content = AgGridUtils.toCSV(values.NiveauDePriorisation, columns);
          parametersValues.NiveauDePriorisation = {
            name: `NiveauDePriorisation.csv`,
            file: null,
            status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD,
            content,
          };
          metadata.NiveauDePriorisation = {
            id: 'NiveauDePriorisation',
            varType: '%DATASETID%',
            options: { connectorId: technicalPolicyParameter?.options?.connectorId },
          };
          valuesToSet.push({ value: values.NiveauDePriorisation, key: 'niveauDePriorisation' });
        }
        if (selectedGisement && !existingGisementsList.includes(selectedGisement)) {
          const newGestionActivites = [...gestionActivites, tableNamesMapping];
          const columns = WizardUtils.getColumnsFormatedForAgGridUtils(TECHNICAL_POLICY_COLUMNS.GestionActivites);
          const content = AgGridUtils.toCSV(newGestionActivites, columns);
          parametersValues.GestionActivites = {
            name: `GestionActivites.csv`,
            file: null,
            status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD,
            content,
          };
          metadata.GestionActivites = {
            id: 'GestionActivites',
            varType: '%DATASETID%',
            options: { connectorId: technicalPolicyParameter?.options?.connectorId },
          };
          valuesToSet.push({ value: newGestionActivites, key: 'gestionActivites' });

          for (const [tableType, table] of Object.entries(tableNamesMapping)) {
            if (!['ClasseActif', 'id'].includes(tableType)) {
              let csvContent;
              const columns = WizardUtils.getColumnsFormatedForAgGridUtils(TECHNICAL_POLICY_COLUMNS[tableType]());
              if (values[table] && values[table].length > 0) {
                csvContent = AgGridUtils.toCSV(values[table], columns);
              } else csvContent = AgGridUtils.toCSV([], columns);
              parametersValues[table] = {
                name: `${table}.csv`,
                file: null,
                status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD,
                content: csvContent,
              };
              metadata[table] = {
                id: table,
                varType: '%DATASETID%',
                options: { connectorId: technicalPolicyParameter?.options?.connectorId },
              };
              valuesToSet.push({ tableType, value: values[table] });
            }
          }
        } else {
          const deletedGisements = values._deletedGisements || [];

          Object.keys(values).forEach((key) => {
            if (!['GestionActivites', 'NiveauDePriorisation', '_deletedGisements'].includes(key)) {
              metadata[key] = {
                id: key,
                varType: '%DATASETID%',
                options: { connectorId: technicalPolicyParameter?.options?.connectorId },
              };
            }
          });
          if (deletedGisements.length > 0) {
            const updatedGestionActivites = gestionActivites.filter((row) => !deletedGisements.includes(row.Gisement));
            const columns = WizardUtils.getColumnsFormatedForAgGridUtils(TECHNICAL_POLICY_COLUMNS.GestionActivites);
            const content = AgGridUtils.toCSV(updatedGestionActivites, columns);
            parametersValues.GestionActivites = {
              name: `GestionActivites.csv`,
              file: null,
              status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD,
              content,
            };
            metadata.GestionActivites = {
              id: 'GestionActivites',
              varType: '%DATASETID%',
              options: { connectorId: technicalPolicyParameter?.options?.connectorId },
            };
            valuesToSet.push({ value: updatedGestionActivites, key: 'gestionActivites' });
          }

          for (const [key, value] of Object.entries(values)) {
            if (
              !['GestionActivites', 'NiveauDePriorisation', '_deletedGisements'].includes(key) ||
              value?.tableType === 'SequencesActionsImposees'
            ) {
              if (value?.tableType === 'SequencesActionsImposees') {
                const tableNamesMappingToSave =
                  value?.gisement === selectedGisement
                    ? tableNamesMapping
                    : allGisements[value?.gisement].tableNamesMapping;
                if (!Object.keys(tableNamesMappingToSave).includes('SequencesActionsImposees')) {
                  const newTableNamesMapping = {
                    ...tableNamesMappingToSave,
                    SequencesActionsImposees: key,
                  };
                  metadata.GestionActivites = {
                    id: 'GestionActivites',
                    varType: '%DATASETID%',
                    options: { connectorId: technicalPolicyParameter?.options?.connectorId },
                  };
                  const gisementIndex = gestionActivites.findIndex((line) => line.Gisement === value?.gisement);
                  const gestionActivitesCopy = clone(gestionActivites);
                  gestionActivitesCopy[gisementIndex] = newTableNamesMapping;
                  const gestionActivitesColumns = WizardUtils.getColumnsFormatedForAgGridUtils(
                    TECHNICAL_POLICY_COLUMNS.GestionActivites
                  );
                  const gestionActivitesContent = AgGridUtils.toCSV(gestionActivitesCopy, gestionActivitesColumns);
                  parametersValues.GestionActivites = {
                    name: `GestionActivites.csv`,
                    file: null,
                    status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD,
                    content: gestionActivitesContent,
                  };
                  valuesToSet.push({ value: gestionActivitesCopy, key: 'gestionActivites' });
                }
                parametersValues[key] = {
                  name: `${key}.csv`,
                  status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD,
                  file: value.file,
                };
              } else {
                const tableType = Object.keys(tableNamesMapping ?? {}).find(
                  (tableType) => tableNamesMapping?.[tableType] === key
                );

                if (!value || equal(gisementData?.[tableType], value) || !tableType || deletedGisements.includes(key))
                  continue;

                valuesToSet.push({ tableType, value, key });
                const columns = WizardUtils.getColumnsFormatedForAgGridUtils(TECHNICAL_POLICY_COLUMNS[tableType]());
                const content = AgGridUtils.toCSV(value, columns);
                parametersValues[key] = {
                  name: `${key}.csv`,
                  file: null,
                  status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD,
                  content,
                };
              }
            }
          }
        }
        if (Object.keys(parametersValues).length > 0) {
          const updateParameterValue = (parameterId, keyToPatch, newValue) => {
            const currentValue = parametersValues[parameterId];
            parametersValues[parameterId] = { ...currentValue, [keyToPatch]: newValue };
          };
          // rethrowing the original error is necessary because the generic function
          // applyPendingOperationsOnFileParameters returns an exception without triggering the catch block
          const error = await FileManagementUtils.applyPendingOperationsOnFileParameters(
            organizationId,
            workspaceId,
            solutionData,
            metadata,
            parametersValues,
            updateParameterValue,
            addDatasetToStore,
            currentScenarioData?.security
          );
          if (error) throw error;
          const currentTechnicalPolicyParameter = currentScenarioData.parametersValues.find(
            (value) => value.parameterId === technicalPolicyParameter?.id
          );
          const parsedCurrentTechnicalPolicyParameterValue = currentTechnicalPolicyParameter?.value
            ? JSON.parse(currentTechnicalPolicyParameter.value)
            : [];
          for (const [key, value] of Object.entries(parametersValues)) {
            const index = parsedCurrentTechnicalPolicyParameterValue.findIndex((value) => value.parameterId === key);
            if (index !== -1) {
              parsedCurrentTechnicalPolicyParameterValue[index] = {
                parameterId: key,
                varType: '%DATASETID%',
                value: value.id,
              };
            } else
              parsedCurrentTechnicalPolicyParameterValue.push({
                parameterId: key,
                varType: '%DATASETID%',
                value: value.id,
              });
          }
          const parameterValuesToUpdate = {
            [technicalPolicyParameter.id]: JSON.stringify(parsedCurrentTechnicalPolicyParameterValue),
          };
          const newTechnicalWizardParameter = ScenarioParametersUtils.buildParametersForUpdate(
            solutionData,
            parameterValuesToUpdate,
            runTemplateParametersIds,
            currentScenarioData,
            scenarios
          );
          let parametersToUpdate;

          if (currentScenarioData.parametersValues) {
            if (currentScenarioData.parametersValues.length === 0) {
              parametersToUpdate = [...newTechnicalWizardParameter];
            } else {
              const technicalWizardParameterIndex = currentScenarioData.parametersValues.findIndex(
                (parameter) => parameter.parameterId === technicalPolicyParameter.id
              );
              if (technicalWizardParameterIndex === -1) {
                parametersToUpdate = [...currentScenarioData.parametersValues, ...newTechnicalWizardParameter];
              } else {
                const currentScenarioParametersValuesCopy = [...currentScenarioData.parametersValues];
                currentScenarioParametersValuesCopy.splice(
                  technicalWizardParameterIndex,
                  1,
                  ...newTechnicalWizardParameter
                );
                parametersToUpdate = [...currentScenarioParametersValuesCopy];
              }
            }
          }
          valuesToSet.forEach((parameterValue) => {
            if (['gestionActivites', 'niveauDePriorisation'].includes(parameterValue.key)) {
              updateTechnicalPolicy(currentScenarioData.id, parameterValue.key, parameterValue.value);
            } else
              updateGisementData(
                currentScenarioData.id,
                parameterValue.value,
                parameterValue.tableType,
                tableNamesMapping.Gisement
              );
          });
          // filter out parameters with null/undefined values
          parametersToUpdate = (parametersToUpdate ?? []).filter((parameter) => parameter.value != null);
          const response = await Api.Scenarios.updateScenario(
            organizationId,
            workspaceId,
            currentScenarioData.id,
            ApiUtils.formatParametersForApi(parametersToUpdate)
          );
          const scenarioPatch = {
            parametersValues: ApiUtils.formatParametersFromApi(response?.data?.parametersValues),
          };
          updateScenario(scenarioPatch);

          for (const [key, value] of Object.entries(values)) {
            if (value?.tableType === 'SequencesActionsImposees') {
              delete values[key];
            }
            if (key === '_deletedGisements') {
              delete values[key];
            }
            if (value === null) {
              delete values[key];
            }
          }
          reset(values);
        }
      } catch (error) {
        console.error(error);
        const errorMessage = "Les données du wizard n'ont pas pu être sauvegardées";
        setSaveTechnicalPolicyError({
          title: error?.response?.data?.title ?? 'Unknown error',
          detail: error?.response?.data?.detail ?? '',
          status: error?.response?.data?.status ?? '',
          comment: errorMessage,
        });
      } finally {
        setWizardQueriesStatus('IDLE');
      }
    },
    [
      addDatasetToStore,
      allGisements,
      currentScenarioData,
      gestionActivites,
      gisementData,
      niveauDePriorisation,
      organizationId,
      runTemplateParametersIds,
      scenarios,
      selectedGisement,
      setWizardQueriesStatus,
      solutionData,
      technicalPolicyParameter.id,
      technicalPolicyParameter?.options?.connectorId,
      updateGisementData,
      updateScenario,
      updateTechnicalPolicy,
      workspaceId,
    ]
  );
  return { saveData };
};
